import { useContext, useMemo, useState, useEffect, useCallback } from "react";
import { AnaliticasContext } from "../../context/AnaliticasContext";
import PanelTitle from "../../components/global/PanelTitle";
import CustomerInline from "../../components/customers/CustomerInline";
import PurchaseInline from "../../components/purchases/PurchaseInline";

const AnaliticasRewards = () => {
  const { customers, counts, getRewardsAnalytics } =
    useContext(AnaliticasContext);
  const [selectedIndex, setSelectedIndex] = useState(null); // 1..12, null => All
  const [filterMode, setFilterMode] = useState(null); // 'level' | 'month' | null

  useEffect(() => {
    getRewardsAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const levels = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // monthMatches no longer used; month filtering relies on customer.months

  const customerHasMonth = useCallback((customer, monthNumber) => {
    // Align with backend semantics: month filters use customer.months only
    const months = customer.months;
    if (months === undefined || months === null) return false;
    if (typeof months === "number") return months === monthNumber;
    const s = String(months).trim();
    if (/^\d+$/.test(s)) return Number(s) === monthNumber;
    // If backend ever sends 'YYYY-MM', compare last two digits
    return s.slice(-2) === String(monthNumber).padStart(2, "0");
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customers)) return [];
    if (selectedIndex === null || filterMode === null) return customers;
    if (filterMode === "level") {
      return customers.filter((c) => Number(c.level || 0) === selectedIndex);
    }
    if (filterMode === "month") {
      return customers.filter((c) => customerHasMonth(c, selectedIndex));
    }
    return customers;
  }, [customers, selectedIndex, filterMode, customerHasMonth]);

  const monthCounts = useMemo(() => {
    if (!Array.isArray(customers)) return levels.map(() => 0);
    return levels.map(
      (i) => customers.filter((c) => customerHasMonth(c, i)).length
    );
  }, [customers, levels, customerHasMonth]);

  const getLastMovement = (customer) => {
    const purchases = Array.isArray(customer.purchases)
      ? customer.purchases
      : [];
    const invoices = Array.isArray(customer.invoices) ? customer.invoices : [];
    const all = [...purchases, ...invoices].filter((x) => x && x.createdAt);
    if (all.length === 0) return null;
    all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return all[0];
  };

  const getCurrentMonthProgressPercent = () => {
    const now = new Date();
    const totalDays = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const elapsedDays = now.getDate();
    return (elapsedDays / totalDays) * 100;
  };

  const renderProgressCell = (lastMovement) => {
    const available = Number(lastMovement?.available_classes || 0);
    const booked = Number(lastMovement?.booked_classes || 0);
    const actualPercent =
      available > 0 ? Math.min(100, (booked / available) * 100) : 0;
    const monthPercent = getCurrentMonthProgressPercent();
    const deficit = monthPercent - actualPercent;
    let colorClass = "bg-success";
    if (deficit > 20) colorClass = "bg-danger";
    else if (deficit > 10) colorClass = "bg-warning";

    return (
      <div className="w-100" style={{ minWidth: 140 }}>
        <div className="progress" style={{ height: 14 }}>
          <div
            className={`progress-bar ${colorClass}`}
            role="progressbar"
            style={{ width: `${actualPercent.toFixed(0)}%` }}
            aria-valuenow={actualPercent.toFixed(0)}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span className="small">{`${actualPercent.toFixed(0)}%`}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderPills = () => {
    return (
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center">
          <button
            className={`me-2 mb-2 btn btn-sm ${
              selectedIndex === null ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => {
              setSelectedIndex(null);
              setFilterMode(null);
            }}
          >
            Todos {Array.isArray(customers) ? `(${customers.length})` : ""}
          </button>
          {levels.map((i) => (
            <div key={i} className="btn-group me-2 mb-2">
              <button
                className={`btn btn-sm ${
                  filterMode === "month" && selectedIndex === i
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => {
                  setSelectedIndex(i);
                  setFilterMode("month");
                }}
                title={`Filtrar por mes ${i}`}
              >
                Mes {i}{" "}
                {typeof monthCounts[i - 1] === "number"
                  ? `(${monthCounts[i - 1]})`
                  : ""}
              </button>
              <button
                className={`btn btn-sm ${
                  filterMode === "level" && selectedIndex === i
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => {
                  setSelectedIndex(i);
                  setFilterMode("level");
                }}
                title={`Filtrar por nivel ${i}`}
              >
                Nivel {i} {counts && counts[i] ? `(${counts[i]})` : ""}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <PanelTitle title="Rewards" callback={getRewardsAnalytics} />
      {renderPills()}

      {!Array.isArray(customers) ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <div className="card p-3 no-scale">
          <div className="table-responsive">
            <table className="table table-sm table-hover">
              <thead>
                <tr className="bold bg-light small">
                  <th className="td-id">ID</th>
                  <th>Cliente</th>
                  <th>Mes</th>
                  <th>Nivel</th>
                  <th>Membresía</th>
                  <th>Compras</th>
                  <th>Cargos</th>
                  <th>Progreso Mes Actual</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c) => {
                  const last_movement = getLastMovement(c);
                  const last_purchase = c.last_purchase || last_movement;
                  return (
                    <tr
                      key={c.customer_id}
                      className="p-2 border-bottom small align-middle hover-light"
                    >
                      <td className="td-id">{c.customer_id}</td>
                      <td>
                        <CustomerInline customer={c} />
                      </td>
                      <td>{Number(c.months || 0)}</td>
                      <td>{Number(c.level || 0)}</td>
                      <td>
                        {last_purchase ? (
                          <PurchaseInline purchase={last_purchase} />
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>
                        {Array.isArray(c.purchases) ? c.purchases.length : 0}
                      </td>
                      <td>
                        {Array.isArray(c.invoices) ? c.invoices.length : 0}
                      </td>
                      <td>{renderProgressCell(last_movement)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnaliticasRewards;
